import React from 'react';
import { QCAttempt, InlineQCStatus, EndlineQCStatus } from '../../types';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { clsx } from 'clsx';

interface Props {
  title: string;
  status: InlineQCStatus | EndlineQCStatus;
  attempts: QCAttempt[];
  isRework: boolean;
}

const statusConfig = {
  'Upcoming': { icon: Clock, color: 'text-slate-400', bg: 'bg-slate-50', label: 'Upcoming' },
  'Pass': { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50', label: 'Pass' },
  'Fail': { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50', label: 'Fail' },
  'Rework': { icon: AlertCircle, color: 'text-amber-600', bg: 'bg-amber-50', label: 'In Rework' },
  'Complete': { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', label: 'Complete' },
};

export const QCDetailsCard: React.FC<Props> = ({ title, status, attempts, isRework }) => {
  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
      {/* Header */}
      <div className={clsx("px-4 py-3 border-b border-slate-100 flex items-center justify-between", config.bg)}>
        <div className="flex items-center gap-2">
          <Icon size={16} className={config.color} />
          <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          {isRework && (
            <span className="px-2 py-0.5 rounded-md bg-amber-100 text-amber-700 text-[9px] font-bold uppercase">
              Rework
            </span>
          )}
          <span className={clsx("px-2 py-0.5 rounded-md text-[9px] font-bold uppercase", config.bg, config.color)}>
            {config.label}
          </span>
        </div>
      </div>

      {/* Attempts List */}
      <div className="p-4">
        {attempts.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-xs">
            <Clock size={24} className="mx-auto mb-2 opacity-50" />
            <p>No QC attempts yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {attempts.map((attempt) => (
              <div key={attempt.attempt} className="border border-slate-100 rounded-lg p-3">
                {/* Attempt Header */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 text-[10px] font-bold flex items-center justify-center">
                      {attempt.attempt}
                    </span>
                    <span className="text-xs font-semibold text-slate-700">
                      Attempt {attempt.attempt}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {attempt.result === 'Pass' ? (
                      <CheckCircle size={14} className="text-green-600" />
                    ) : (
                      <XCircle size={14} className="text-red-600" />
                    )}
                    <span className={clsx(
                      "text-[10px] font-bold uppercase",
                      attempt.result === 'Pass' ? 'text-green-600' : 'text-red-600'
                    )}>
                      {attempt.result}
                    </span>
                  </div>
                </div>

                {/* Date */}
                <div className="text-[10px] text-slate-500 mb-2">
                  {new Date(attempt.at).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>

                {/* Defects */}
                {(() => {
                  const rawDefects = attempt.defects;
                  if (!rawDefects || !Array.isArray(rawDefects)) return null;

                  const tags: string[] = [];
                  rawDefects.forEach(d => {
                    const category = d.category || 'General';
                    tags.push(`${category}: ${d.item} (${d.count})`);
                  });

                  if (tags.length === 0) return null;

                  return (
                    <div className="mt-2 pt-2 border-t border-slate-100">
                      <p className="text-[9px] text-slate-400 uppercase font-bold mb-1.5">Defects</p>
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((tag, idx) => (
                          <div key={idx} className="px-2 py-1 rounded-md bg-red-50 border border-red-100">
                            <span className="text-[10px] font-semibold text-red-700">
                              {tag}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {attempts.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <span className="text-[10px] text-slate-500 font-medium">
            Total Attempts: {attempts.length}
          </span>
          <span className="text-[10px] text-slate-500 font-medium">
            Defects: {attempts.reduce((sum, a) => {
              if (!a.defects || !Array.isArray(a.defects)) return sum;
              let attemptTotal = 0;
              a.defects.forEach(d => {
                attemptTotal += Number(d.count) || 0;
              });
              return sum + attemptTotal;
            }, 0)}
          </span>
        </div>
      )}
    </div>
  );
};
